// card-sandbox: fetch the runtime card index (/apps/cards.json) and resolve a
// kind's entry {app_id, url, function_sets}. The index maps kinds a build has no
// plugin for to the package that serves them; CardFor uses the entry to render the
// card in a sandboxed iframe (SandboxedCard).
//
// One cached fetch for the whole app (the index is small and changes only when a
// package is (un)published). Mirrors runtime.js's index cache — this supersedes
// that module's `<script>`-injection path.

let _indexPromise = null;
// Kinds we've already re-fetched the index for on a miss — so a genuinely-absent
// kind (a real GenericCard) re-fetches at most ONCE, never in a loop.
const _missRetried = new Set();

const resolved = (entry) => !!(entry && (entry.url || entry.renderer));

// The resolved index, kept alongside the promise so a SYNCHRONOUS caller can ask
// about a kind. `cardFreshnessKey` runs during render and cannot await — see the
// note there for why the answer matters.
let _index = null;

function fetchIndex() {
  if (!_indexPromise) {
    // `cache: 'no-cache'` revalidates (conditional GET) rather than serving a
    // heuristically-cached copy — /apps/cards.json has no Cache-Control, so without
    // this a newly-PUBLISHED card kind (e.g. a Tier-3 artifact) never reaches a page
    // that loaded before the publish, and its card renders blank via GenericCard.
    _indexPromise = fetch('/apps/cards.json', { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}))
      .then((idx) => { _index = idx || {}; return _index; });
  }
  return _indexPromise;
}

/**
 * The card-index entry for `kind` IF the index has already loaded, else undefined.
 *
 * Deliberately does not fetch: it exists for callers that run during render and
 * cannot await. `undefined` means "not known yet", which is a different answer from
 * `null` ("no runtime card for this kind") and callers must distinguish them.
 */
export function cardIndexEntrySync(kind) {
  if (!_index) return undefined;
  const entry = _index[kind];
  return resolved(entry) ? entry : null;
}

// Resolve a kind to its card-index entry, or null if there's no runtime card for
// it (→ CardFor falls back to GenericCard). Never throws.
export async function cardIndexEntry(kind) {
  try {
    let index = await fetchIndex();
    let entry = index && index[kind];
    // A miss may just mean the in-memory index is stale (the kind was published
    // after this page loaded). Bust the cache and re-fetch ONCE per kind before
    // concluding there's no runtime card — so a fresh card kind resolves live,
    // no hard refresh needed.
    // canvas-card: an entry is resolved if it names either a bundle to fetch (`url`) or a
    // renderer this build already carries (`renderer`). Testing `url` alone treated every
    // renderer card as a miss, so it re-fetched the index once and then fell through to
    // GenericCard — a card that had published perfectly, rendered as plain text.
    if (!resolved(entry) && !_missRetried.has(kind)) {
      _missRetried.add(kind);
      _indexPromise = null;
      index = await fetchIndex();
      entry = index && index[kind];
    }
    return resolved(entry) ? entry : null;
  } catch (e) {
    return null;
  }
}

// gallery-efficiency: given a RESOLVED runtime index entry, does its sandboxed
// card need a live backend channel? Author-declared `mode` wins; absent (mesh
// cards don't publish it yet) → derive from function_sets, since a card gated to
// emit nothing can't need a channel. A null entry → false, but callers that gate
// a socket on this must first decide what a MISSING entry means for THEIR card
// (build-time / GenericCard kinds still act — see index.js `embedCardNeedsChannel`,
// which is fail-safe). See ADR 2026-08-13-gallery-efficiency.
export function entryNeedsChannel(entry) {
  if (!entry) return false;
  if (entry.mode === 'interactive') return true;
  if (entry.mode === 'display') return false;
  return !!(entry.function_sets && entry.function_sets.length);
}

// --- test seams ---
export function _setIndex(index) { _indexPromise = Promise.resolve(index || {}); _missRetried.clear(); }
export function _resetIndexCache() { _indexPromise = null; _missRetried.clear(); }
